import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { SubmitHandler } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { errorMessageFor } from 'utils/forms.utils';

import { SAUDI_REWARDS_FORGOT_PIN_URL } from 'constants/saudi-rewards.constants';
import { useAccount, useSaudiRewardsBalance } from 'restful/accounts';

import { SaudiRewardsFormFields } from 'components/@account/types';
import BorderStack from 'components/@common/BorderStack';
import Dialog from 'components/@common/Dialog';
import Link from 'components/@common/Link';
import Form from 'components/@forms/Form';
import FormOTPField from 'components/@forms/FormOTPField';
import SaudiRewardsCardLandscape from 'components/@saudi-rewards/SaudiRewardsCardLandscape';
import FormattedMoneyInfo from 'components/@tickets/FormattedMoneyInfo';

import SettingLinksList from './SettingLinksList';

interface Props {
  open: boolean;
  onClose: () => void;
}

const SaudiRewardsBalanceDialog = ({ open, onClose }: Props) => {
  const { account } = useAccount();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { balance, requestBalance, isLoading } = useSaudiRewardsBalance();

  const handleSubmit: SubmitHandler<SaudiRewardsFormFields> = (data) => {
    requestBalance(
      { memberPin: data.saudiRewardsMemberPin },
      {
        onError: () => {
          enqueueSnackbar(
            intl.formatMessage({ id: 'api.error.saudi.rewards' }),
            { variant: 'error' },
          );
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} transitionDirection="left">
      <DialogTitle>
        <FormattedMessage id="account.saudi.rewards.title" />
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100% - 72px)', md: 'auto' },
        }}
      >
        <Stack sx={{ mb: 2 }}>
          <SaudiRewardsCardLandscape
            saudiRewardsMemberId={account?.saudiRewardsMemberId ?? undefined}
          />
        </Stack>
        {balance !== undefined ? (
          <Box sx={{ flexGrow: { xs: 1, md: 0 } }}>
            <BorderStack sx={{ gap: 1 }}>
              <Typography
                variant="small"
                sx={{
                  color: 'text.secondary',
                }}
              >
                <FormattedMessage id="saudi.rewards.balance.label" />
              </Typography>
              <Typography variant="h6">
                <FormattedMessage
                  id="saudi.rewards.balance"
                  values={{
                    balance: (
                      <FormattedMoneyInfo
                        price={balance.balance}
                        style="decimal"
                        minimumFractionDigits={0}
                      />
                    ),
                  }}
                />
              </Typography>
            </BorderStack>
          </Box>
        ) : (
          <Form
            onSubmit={handleSubmit}
            defaultValues={{ saudiRewardsMemberPin: '' }}
            sx={{ flexGrow: { xs: 1, md: 0 } }}
          >
            <Stack
              sx={{
                gap: 1,
                mb: 2,
              }}
            >
              <BorderStack sx={{ py: 3, gap: 2 }}>
                <Typography
                  variant="small"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  <FormattedMessage id="saudi.rewards.check.balance.title" />
                </Typography>
                <FormOTPField
                  name="saudiRewardsMemberPin"
                  rules={{
                    validate: (value) =>
                      value.length === 4
                        ? true
                        : errorMessageFor(intl, {
                            type: 'min_length',
                            label: 'pin',
                            value: 4,
                          }),
                    required: errorMessageFor(intl, {
                      type: 'required',
                      label: 'pin',
                    }),
                  }}
                  type="password"
                  autoFocus
                  codeLength={4}
                />
                <Button
                  variant="outlined"
                  color="secondary"
                  type="submit"
                  loading={isLoading}
                  sx={{ width: { md: 'fit-content' } }}
                >
                  <FormattedMessage id="saudi.rewards.check.balance.button" />
                </Button>
                <Link
                  href={SAUDI_REWARDS_FORGOT_PIN_URL}
                  target="_blank"
                  variant="small"
                  underline="always"
                  color="text.secondary"
                >
                  <FormattedMessage id="saudi.rewards.link.form.forgot.pin" />
                </Link>
              </BorderStack>
            </Stack>
          </Form>
        )}
        <SettingLinksList onCloseCheckBalance={onClose} />
      </DialogContent>
    </Dialog>
  );
};

export default SaudiRewardsBalanceDialog;
